import React, { useState, useEffect, useRef } from "react"
import axios from "axios"
import { useHistory } from "react-router-dom"
import { isMobile } from "react-device-detect"
import {
  ClipboardList,
  Users2,
  CheckCircle2,
  Loader2,
  RefreshCw,
} from "lucide-react"
import "./feedback.css"

import Logo from "../../assets/images/brands/converge.png"

const ADMIN_BASE = isMobile
  ? process.env.REACT_APP_ADMIN
  : process.env.REACT_APP_ADMIN_LAPTOP

// ── Static question config ──────────────────────────────────────────────
// Edit these arrays to change wording/options — nothing else needs to change.

const HOW_HEARD_OPTIONS = [
  "Email campaign",
  "Walmart Global Tech LinkedIn page",
  "Word of mouth (Friend/ Colleague)",
  "Walmart Global Tech Website",
  "Other, if so please tell us more",
]

const RATING_ASPECTS = [
  "Diversity of speakers",
  "Session relevance",
  "Learning opportunity",
  "Experience Zone",
  "Networking and connections",
  "Overall event experience",
]

const RATING_FACTORS = [
  "Best in class technology company",
  "Company culture",
  "Making a difference in society",
  "Innovative company",
  "Career growth opportunities",
  "Others",
]

const NETWORKING_IMPROVEMENTS = [
  "Better matching with relevant attendees",
  "More time for networking",
  "Smaller, topic-based groups",
  "Easier access to Walmart leaders",
  "It worked well for me",
]

const EXPERTISE_AREAS = [
  "Software Engineering (Individual Contributor)",
  "Software Engineering (People Leader)",
  "Data Analytics",
  "Data Engineering and Architecture",
  "Product Management",
  "Technical Product Management",
  "Other",
]

// ── Small reusable inputs ───────────────────────────────────────────────

// Numeric 1-10 (or any range) scale — used for recommendScore / employerScore.
const ScaleRating = ({ min = 1, max = 5, value, onChange, lowLabel, highLabel }) => {
  const nums = []
  for (let i = min; i <= max; i++) nums.push(i)
  return (
    <div className="fb-scale-wrap">
      <div className="fb-scale-row">
        {nums.map(n => (
          <button
            type="button"
            key={n}
            className={`fb-scale-btn ${value === n ? "active" : ""}`}
            onClick={() => onChange(n)}
          >
            {n}
          </button>
        ))}
      </div>
      {(lowLabel || highLabel) && (
        <div className="fb-scale-labels">
          <span className={`fb-scale-label ${value === min ? "glow" : ""}`}>
            {lowLabel}
          </span>
          <span className={`fb-scale-label ${value === max ? "glow" : ""}`}>
            {highLabel}
          </span>
        </div>
      )}
    </div>
  )
}

// ── Animated 1-5 Star Rating widget ─────────────────────────────────────
// Ports the provided vanilla StarRating logic: clicking a star cascades a
// staggered reveal animation across any newly-lit stars (skipping ahead
// from the previous value), each star bursts a ring + bounces its fill +
// fades its outline stroke + fires a radial line burst. Hovering previews
// the outline (no fill, no animation) up to the hovered star. The current
// rating's label ("Terrible".."Excellent") is shown below, following
// hover when present, else the committed value.

const STAR_RATING_LABELS = [
  { id: 1, name: "Terrible" },
  { id: 2, name: "Bad" },
  { id: 3, name: "OK" },
  { id: 4, name: "Good" },
  { id: 5, name: "Excellent" },
]

const slugify = str =>
  str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")

const StarRating = ({ name, value, onChange }) => {
  // Tracks the previous committed rating so we know which stars are
  // "newly lit" on a jump (e.g. going straight from 1 to 4) and should
  // get a staggered animation-delay, exactly mirroring the original
  // StarRating.updateRating() logic.
  const prevValueRef = useRef(0)
  const [delayMap, setDelayMap] = useState({})
  const [hoverId, setHoverId] = useState(0)

  const handleSelect = newId => {
    const prevId = prevValueRef.current || 0
    const map = {}
    let delay = 0
    for (let id = 1; id <= 5; id++) {
      if (id > prevId + 1 && id <= newId) {
        delay += 1
        map[id] = delay
      }
    }
    setDelayMap(map)
    prevValueRef.current = newId
    onChange(newId)
  }

  const displayedId = hoverId || value || 0
  const displayedLabel = STAR_RATING_LABELS.find(r => r.id === displayedId)

  return (
    <div className="str-wrap">
      <div className="str-stars" onMouseLeave={() => setHoverId(0)}>
        {STAR_RATING_LABELS.map(r => {
          const isLit = value >= r.id
          const isHoverPreview = hoverId > 0 && hoverId >= r.id

          return (
            <label
              key={r.id}
              className={[
                "str-label",
                isLit ? "str-label--lit" : "",
                !isLit && isHoverPreview ? "str-label--hover" : "",
                delayMap[r.id] ? `str-label--delay${delayMap[r.id]}` : "",
              ]
                .join(" ")
                .trim()}
              onMouseEnter={() => setHoverId(r.id)}
            >
              <input
                type="radio"
                className="str-input"
                name={name}
                value={r.id}
                checked={value === r.id}
                onChange={() => handleSelect(r.id)}
              />
              <svg
                className="str-star"
                width="32"
                height="32"
                viewBox="0 0 32 32"
                aria-hidden="true"
              >
                <g transform="translate(16,16)">
                  <circle
                    className="str-star-ring"
                    fill="none"
                    strokeWidth="16"
                    r="8"
                    transform="scale(0)"
                  />
                </g>
                <g strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <g transform="translate(16,16) rotate(180)">
                    <polygon
                      className="str-star-stroke"
                      points="0,15 4.41,6.07 14.27,4.64 7.13,-2.32 8.82,-12.14 0,-7.5 -8.82,-12.14 -7.13,-2.32 -14.27,4.64 -4.41,6.07"
                      fill="none"
                    />
                    <polygon
                      className="str-star-fill"
                      points="0,15 4.41,6.07 14.27,4.64 7.13,-2.32 8.82,-12.14 0,-7.5 -8.82,-12.14 -7.13,-2.32 -14.27,4.64 -4.41,6.07"
                    />
                  </g>
                  <g
                    transform="translate(16,16)"
                    strokeDasharray="12 12"
                    strokeDashoffset="12"
                  >
                    {[0, 72, 144, 216, 288].map(deg => (
                      <polyline
                        key={deg}
                        className="str-star-line"
                        transform={`rotate(${deg})`}
                        points="0 4,0 16"
                      />
                    ))}
                  </g>
                </g>
              </svg>
              <span className="str-sr">
                {r.id} star{r.id > 1 ? "s" : ""} — {r.name}
              </span>
            </label>
          )
        })}
      </div>
      <p className="str-display">
        {displayedLabel ? displayedLabel.name : "\u00A0"}
      </p>
    </div>
  )
}

const CheckboxGroup = ({ options, value = [], onChange, otherText, onOtherText }) => {
  const toggle = opt => {
    if (value.includes(opt)) onChange(value.filter(v => v !== opt))
    else onChange([...value, opt])
  }
  const otherSelected = value.some(v => v.toLowerCase().startsWith("other"))
  return (
    <div className="fb-options">
      {options.map(opt => (
        <label
          key={opt}
          className={`fb-checkbox ${value.includes(opt) ? "selected" : ""}`}
        >
          <input
            type="checkbox"
            checked={value.includes(opt)}
            onChange={() => toggle(opt)}
          />
          <span>{opt}</span>
        </label>
      ))}
      {otherSelected && (
        <input
          type="text"
          className="fb-text-input fb-other-input"
          placeholder="Please tell us more"
          value={otherText || ""}
          onChange={e => onOtherText(e.target.value)}
        />
      )}
    </div>
  )
}

const RadioGroup = ({ options, value, onChange, otherText, onOtherText }) => {
  const otherSelected = value && value.toLowerCase().startsWith("other")
  return (
    <div className="fb-options">
      {options.map(opt => (
        <label
          key={opt}
          className={`fb-radio ${value === opt ? "selected" : ""}`}
        >
          <input
            type="radio"
            name={`${opt}-radio-group`}
            checked={value === opt}
            onChange={() => onChange(opt)}
          />
          <span>{opt}</span>
        </label>
      ))}
      {otherSelected && (
        <input
          type="text"
          className="fb-text-input fb-other-input"
          placeholder="Please tell us more"
          value={otherText || ""}
          onChange={e => onOtherText(e.target.value)}
        />
      )}
    </div>
  )
}

// ── Event Feedback form ───────────────────────────────────────────────────
// NOTE: back navigation is handled entirely by the page header — this
// form no longer renders its own back link.

const EventFeedbackForm = ({ obj, onSubmitted }) => {
  const [name, setName] = useState(obj?.Firstname || "")
  const [howHeard, setHowHeard] = useState([])
  const [howHeardOther, setHowHeardOther] = useState("")
  const [ratings, setRatings] = useState({})
  const [recommendScore, setRecommendScore] = useState(null)
  const [employerScore, setEmployerScore] = useState(null)
  const [factors, setFactors] = useState([])
  const [factorsOther, setFactorsOther] = useState("")
  const [comments, setComments] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState("")

  const setRating = (aspect, val) =>
    setRatings(prev => ({ ...prev, [aspect]: val }))

  const validate = () => {
    if (!name.trim()) return "Please enter your name."
    if (!howHeard.length) return "Please tell us how you heard about Converge."
    if (RATING_ASPECTS.some(a => !ratings[a]))
      return "Please rate every aspect of the session."
    if (!recommendScore) return "Please rate how likely you are to recommend Converge."
    if (!employerScore) return "Please rate Walmart as an employer of choice."
    if (!factors.length) return "Please select at least one factor that influenced your rating."
    return ""
  }

  const handleSubmit = async () => {
    const validationError = validate()
    if (validationError) {
      setError(validationError)
      return
    }
    setError("")
    setSubmitting(true)

    const answers = {
      name,
      howHeard,
      howHeardOther: howHeard.some(v => v.toLowerCase().startsWith("other"))
        ? howHeardOther
        : "",
      sessionRatings: ratings,
      recommendScore,
      employerScore,
      factors,
      factorsOther: factors.some(v => v.toLowerCase().startsWith("other"))
        ? factorsOther
        : "",
      comments,
    }

    try {
      await axios.post(`${ADMIN_BASE}/feedbackForm/submit`, {
        email: obj?.EmailAddress,
        name,
        ticketVariant: obj?.Ticket_Variant,
        formType: "Feedback",
        answers,
      })
      onSubmitted()
    } catch (err) {
      setError(
        err?.response?.data?.message || "Something went wrong submitting your feedback."
      )
    } finally {
      setSubmitting(false)
    }
  }

  let __i = 0

  return (
    <div className="fb-card">
      <h2 className="fb-form-title">Event Feedback</h2>

      <div className="fb-field" style={{ "--i": __i++ }}>
        <label>Name</label>
        <input
          type="text"
          className="fb-text-input"
          value={name}
          onChange={e => setName(e.target.value)}
        />
      </div>

      <div className="fb-field" style={{ "--i": __i++ }}>
        <label>Email ID</label>
        <input
          type="text"
          className="fb-text-input"
          value={obj?.EmailAddress || ""}
          disabled
        />
      </div>

      <div className="fb-field" style={{ "--i": __i++ }}>
        <label>How did you hear about Converge @ Walmart?</label>
        <CheckboxGroup
          options={HOW_HEARD_OPTIONS}
          value={howHeard}
          onChange={setHowHeard}
          otherText={howHeardOther}
          onOtherText={setHowHeardOther}
        />
      </div>

      <div className="fb-field" style={{ "--i": __i++ }}>
        <label>
          On a scale of 1 to 5, where 5 means "Exceeded expectations" and 1
          means "Did not meet expectations", how would you rate the
          following:
        </label>
        {RATING_ASPECTS.map(aspect => (
          <div key={aspect} className="fb-subfield">
            <span className="fb-subfield-label">{aspect}</span>
            <StarRating
              name={`aspect-${slugify(aspect)}`}
              value={ratings[aspect]}
              onChange={val => setRating(aspect, val)}
            />
          </div>
        ))}
      </div>

      <div className="fb-field" style={{ "--i": __i++ }}>
        <label>
          How likely are you to recommend Converge @ Walmart to a colleague
          or a friend?
        </label>
        <ScaleRating
          min={1}
          max={10}
          value={recommendScore}
          onChange={setRecommendScore}
          lowLabel="Not at all likely"
          highLabel="Extremely likely"
        />
      </div>

      <div className="fb-field" style={{ "--i": __i++ }}>
        <label>
          On a scale of 1 to 10, how likely are you to consider Walmart (or
          recommend it) as an employer of choice in the future?
        </label>
        <ScaleRating
          min={1}
          max={10}
          value={employerScore}
          onChange={setEmployerScore}
          lowLabel="Not at all likely"
          highLabel="Extremely likely"
        />
      </div>

      <div className="fb-field" style={{ "--i": __i++ }}>
        <label>What were the key factors that influenced your rating?</label>
        <CheckboxGroup
          options={RATING_FACTORS}
          value={factors}
          onChange={setFactors}
          otherText={factorsOther}
          onOtherText={setFactorsOther}
        />
      </div>

      <div className="fb-field" style={{ "--i": __i++ }}>
        <label>If you have any additional comments, please let us know (optional)</label>
        <textarea
          className="fb-textarea"
          rows={4}
          value={comments}
          onChange={e => setComments(e.target.value)}
        />
      </div>

      {error && <div className="fb-error">{error}</div>}

      <button className="fb-submit-btn" onClick={handleSubmit} disabled={submitting}>
        {submitting ? <Loader2 className="fb-spin" size={16} /> : null}
        {submitting ? "Submitting..." : "Submit Feedback"}
      </button>
    </div>
  )
}

// ── Networking Feedback form ──────────────────────────────────────────────
// NOTE: back navigation is handled entirely by the page header — this
// form no longer renders its own back link.

const NetworkingFeedbackForm = ({ obj, onSubmitted }) => {
  const [valueScore, setValueScore] = useState(null)
  const [improvements, setImprovements] = useState([])
  const [expertise, setExpertise] = useState("")
  const [expertiseOther, setExpertiseOther] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState("")

  const validate = () => {
    if (!valueScore) return "Please rate how valuable the networking session was."
    if (!improvements.length) return "Please select at least one option."
    if (!expertise) return "Please select your area of expertise."
    return ""
  }

  const handleSubmit = async () => {
    const validationError = validate()
    if (validationError) {
      setError(validationError)
      return
    }
    setError("")
    setSubmitting(true)

    const answers = {
      valueScore,
      improvements,
      expertise,
      expertiseOther: expertise.toLowerCase().startsWith("other")
        ? expertiseOther
        : "",
    }

    try {
      await axios.post(`${ADMIN_BASE}/feedbackForm/submit`, {
        email: obj?.EmailAddress,
        name: obj?.Firstname,
        ticketVariant: obj?.Ticket_Variant,
        formType: "NetworkingFeedback",
        answers,
      })
      onSubmitted()
    } catch (err) {
      setError(
        err?.response?.data?.message || "Something went wrong submitting your feedback."
      )
    } finally {
      setSubmitting(false)
    }
  }

  let __i = 0

  return (
    <div className="fb-card">
      <h2 className="fb-form-title">Networking Feedback</h2>

      <div className="fb-field" style={{ "--i": __i++ }}>
        <label>
          How valuable was the networking session in helping you connect
          with peers or Walmart leaders?
        </label>
        <StarRating
          name="networking-value"
          value={valueScore}
          onChange={setValueScore}
        />
      </div>

      <div className="fb-field" style={{ "--i": __i++ }}>
        <label>
          What could improve the networking experience at future Converge
          events? (Select more than one)
        </label>
        <CheckboxGroup
          options={NETWORKING_IMPROVEMENTS}
          value={improvements}
          onChange={setImprovements}
        />
      </div>

      <div className="fb-field" style={{ "--i": __i++ }}>
        <label>Which of the following areas best represents your expertise?</label>
        <RadioGroup
          options={EXPERTISE_AREAS}
          value={expertise}
          onChange={setExpertise}
          otherText={expertiseOther}
          onOtherText={setExpertiseOther}
        />
      </div>

      {error && <div className="fb-error">{error}</div>}

      <button className="fb-submit-btn" onClick={handleSubmit} disabled={submitting}>
        {submitting ? <Loader2 className="fb-spin" size={16} /> : null}
        {submitting ? "Submitting..." : "Submit Feedback"}
      </button>
    </div>
  )
}

// ── Success / already-submitted popup ──────────────────────────────────────
// Full-page overlay. Shown once a submission completes the flow for this
// user (single form for non-NYUC, both forms for NYUC) — also shown
// immediately on page load if the status check finds that condition
// already true, so a fully-done user can never get back into a form.
const SuccessPopup = ({ kind, onRefresh, onDashboard }) => (
  <div className="fb-popup-overlay">
    <div className="fb-popup-card">
      <CheckCircle2 size={52} className="fb-popup-icon" />
      <h3 className="fb-popup-title">
        {kind === "both"
          ? "Both feedbacks submitted!"
          : "Feedback submitted!"}
      </h3>
      <p className="fb-popup-text">
        {kind === "both"
          ? "You've successfully submitted both the Event Feedback and Networking Feedback. Thank you for your time!"
          : "You've successfully submitted your feedback. Thank you for your time!"}
      </p>
      {/* <p className="fb-popup-hint">Please refresh to continue.</p> */}
      <div className="fb-popup-actions">
        {/* <button className="fb-popup-btn  fb-popup-btn-ghost" onClick={onRefresh}>
          <RefreshCw size={15} /> Refresh Page
        </button> */}
        <button className="fb-popup-btn fb-popup-btn-primary" onClick={onDashboard}>
          Back to Dashboard
        </button>
      </div>
    </div>
  </div>
)

// ── Thank you screen (fallback — normally the popup covers this) ──────────

const ThankYou = ({ onDone }) => (
  <div className="fb-card fb-thankyou">
    <CheckCircle2 size={48} className="fb-thankyou-icon" />
    <h2 className="fb-form-title">Thanks for your feedback!</h2>
    <p className="fb-thankyou-text">
      Want more updates? Follow us{" "}
      <a
        href="https://twitter.com/WalmartGlobalTech"
        target="_blank"
        rel="noreferrer"
      >
        @WalmartGlobalTech
      </a>{" "}
      or visit{" "}
      <a href="https://tech.walmart.com" target="_blank" rel="noreferrer">
        tech.walmart.com
      </a>
      .
    </p>
    <button className="fb-submit-btn" onClick={onDone}>
      Done
    </button>
  </div>
)

// ── Main page ──────────────────────────────────────────────────────────────

const Feedback = () => {
  const history = useHistory()
  const obj = JSON.parse(localStorage.getItem("authUser") || "{}")
  // Ticket variants that get access to BOTH feedback forms (case-insensitive substring match)
const BOTH_FORMS_VARIANTS = ["yellow", "green", "nyuc"]

const ticketVariant = (obj?.Ticket_Variant || "").toLowerCase()
const isNYUC = BOTH_FORMS_VARIANTS.some(v => ticketVariant.includes(v))

  // view: "loading" | "select" | "event" | "networking" | "done"
  const [view, setView] = useState("loading")
  const [submittedTypes, setSubmittedTypes] = useState([])
  const [statusLoaded, setStatusLoaded] = useState(false)

  // popup: null | "single" | "both"
  const [popup, setPopup] = useState(null)

  useEffect(() => {
    if (!obj?.EmailAddress) {
      // No user context — fall back to whatever the ticket variant implies
      setView(isNYUC ? "select" : "event")
      setStatusLoaded(true)
      return
    }

    axios
      .get(`${ADMIN_BASE}/feedbackForm/status`, {
        params: { email: obj.EmailAddress },
      })
      .then(res => {
        const types = res.data?.submittedTypes || []
        setSubmittedTypes(types)

        if (isNYUC) {
          setView("select")
          if (types.includes("Feedback") && types.includes("NetworkingFeedback")) {
            setPopup("both")
          }
        } else {
          if (types.includes("Feedback")) {
            setPopup("single")
            setView("done")
          } else {
            setView("event")
          }
        }
      })
      .catch(err => {
        console.error("Failed to fetch feedback status:", err)
        // If status check fails, don't block the user from filling the form
        setView(isNYUC ? "select" : "event")
      })
      .finally(() => setStatusLoaded(true))
  }, [])

  const goBackToDashboard = () => history.push("/page/lobby")
  const refreshPage = () => window.location.reload()

  // ── Header back-link routing ────────────────────────────────────────────
  // NYUC users on the selection screen (or non-NYUC users, who never see a
  // selection screen at all) go straight to the lobby. NYUC users who are
  // inside either form go back to the selection screen instead — the forms
  // themselves no longer render their own back link.
  const handleHeaderBack = () => {
    if (isNYUC && (view === "event" || view === "networking")) {
      setView("select")
    } else {
      goBackToDashboard()
    }
  }

  const handleFormSubmitted = formType => {
    const updated = [...new Set([...submittedTypes, formType])]
    setSubmittedTypes(updated)

    if (isNYUC) {
      if (updated.includes("Feedback") && updated.includes("NetworkingFeedback")) {
        setPopup("both")
      } else {
        // Only one of the two done so far — no popup yet, just go back
        // to the selection screen so the completed card shows gold.
        setView("select")
      }
    } else {
      setPopup("single")
      setView("done")
    }
  }

  if (!statusLoaded) {
    return (
      <div className="feedback-page">
        <div className="fb-page-loading">
          <Loader2 className="fb-spin" size={28} />
        </div>
      </div>
    )
  }

  return (
    <div className="feedback-page">
      {popup && (
        <SuccessPopup
          kind={popup}
          onRefresh={refreshPage}
          onDashboard={goBackToDashboard}
        />
      )}

      <div className="feedback-page-header">
        <button className="fb-back-link" onClick={handleHeaderBack}>
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28"
            fill="white" viewBox="0 0 24 24" >
            <path d="M12 2C6.49 2 2 6.49 2 12s4.49 10 10 10 10-4.49 10-10S17.51 2 12 2m0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8"></path><path d="m12.71 8.71-1.42-1.42L6.59 12l4.7 4.71 1.42-1.42-2.3-2.29H17v-2h-6.59z"></path>
          </svg>
        </button>
        <span className="fb-page-title">Feedback</span>
      </div>

      <div className="feedback-page-body">
        {view === "select" && (
          <div className="fb-select-grid">
            {(() => {
              const eventDone = submittedTypes.includes("Feedback")
              return (
                <button
                  className={`fb-select-card ${eventDone ? "completed" : ""}`}
                  onClick={() => !eventDone && setView("event")}
                  disabled={eventDone}
                >
                  {eventDone && (
                    <span className="fb-completed-badge">
                      <CheckCircle2 size={14} /> Submitted
                    </span>
                  )}
                  <ClipboardList size={28} />
                  <span className="fb-select-card-title">Event Feedback</span>
                  <span className="fb-select-card-sub">
                    Share your thoughts on the sessions and overall event
                  </span>
                </button>
              )
            })()}

            {(() => {
              const netDone = submittedTypes.includes("NetworkingFeedback")
              return (
                <button
                  className={`fb-select-card ${netDone ? "completed" : ""}`}
                  onClick={() => !netDone && setView("networking")}
                  disabled={netDone}
                >
                  {netDone && (
                    <span className="fb-completed-badge">
                      <CheckCircle2 size={14} /> Submitted
                    </span>
                  )}
                  <Users2 size={28} />
                  <span className="fb-select-card-title">Networking Feedback</span>
                  <span className="fb-select-card-sub">
                    Tell us about your networking experience
                  </span>
                </button>
              )
            })()}
          </div>
        )}

        {view === "event" && (
          <EventFeedbackForm
            obj={obj}
            onSubmitted={() => handleFormSubmitted("Feedback")}
          />
        )}

        {view === "networking" && (
          <NetworkingFeedbackForm
            obj={obj}
            onSubmitted={() => handleFormSubmitted("NetworkingFeedback")}
          />
        )}

        {view === "done" && !popup && (
          <ThankYou onDone={goBackToDashboard} />
        )}
      </div>

      <div className="feedback-page-footer">
        <span className="fb-page-title">Walmart</span>
        <img src={Logo}/>
      </div>
    </div>
  )
}

export default Feedback